import { useEffect, useState } from "react";
import HorizontalTab from "../common/HorizontalTab/horizontalTab";
import pumpIcon from "../../assets/images/determine-pump.svg";
import ButtonGroup from "../common/button-group";
import { useAppSelector } from "../../store/hooks/StoreHooks";
import { useSelector, useStore } from "react-redux";
import { RootState } from "../../store/ConfigureStore";
import useReportFacade from "../../facade/report/report.facade";
import Loader from "../common/loader";
import DeviationSurvey from "./DeviationSurvey";
import SizingSchematic from "./SizingSchematic";
import PerformanceCurve from "./PerformanceCurve";
import WellInfoPerformanceReport from "./WellInfoPerformanceReport";
import EquipmentDetails from "./EquipmentDetails";
import "./report.scss";
import { getMainReportDataFromUtility } from "../../utilities/commonUtilities";
import DropdownButton from "../common/dropdown-button";

const tabs = ['Well depths', 'Well / IPR @Datum', 'Operating performance', 'Fluid properties', 'Downhole operating performance'];

const SizingSummary: React.FC = () => {
    const {
        downloadMainReports
    } = useReportFacade();
    const store = useStore<RootState>();
    const [activeTab, setActiveTab] = useState(0);
    const [reportInfo, setReportInfo] = useState<any>(null);

    const [downloadReportData, setDownloadReportData] = useState<any>(null);
    const mainReportOptions = [
        { name: 'Acrobat(PDF) file', value: 'PDF' },
        { name: 'CSV (comma delimited)', value: 'CSV' },
        { name: 'Rich Text Format', value: 'RTF' },
        { name: 'TIFF file', value: 'IMAGE' },

    ];
    const loading = useSelector((state: RootState) => state.reports.loading);
    const { tab1Input } = useAppSelector((state) => state?.tubing);
    const projectDetails: any = useAppSelector((state) => state?.projectTab?.tab4Input);
    
    const MainReportData = () => {
        const sateData = store.getState();
        let mainReportData = getMainReportDataFromUtility(sateData);
        setReportInfo(mainReportData?.inputs);
        setDownloadReportData(mainReportData);

    };

    useEffect(() => {
        if (projectDetails && tab1Input?.projectIDNew) {
            MainReportData();
        }
    }, [projectDetails, tab1Input]);

    const DownloadMainReport = (reportType: string) => {
        downloadMainReports({ ...downloadReportData, reportType });
    };
    
    return (
      <>
        <div className='flex flex-col gap-[16px] w-full p-2'>
          <div className='flex justify-end w-full border-b-[1px] border-darkcolor-400 pb-[16px]'>
            <Loader isLoading={loading} />
            <DropdownButton
              mainButtonLabel='Print'
              mainButtonlClick={() => DownloadMainReport("PDF")}
              optionClick={DownloadMainReport}
              options={mainReportOptions}
              className="bg-primary-500"
            />
          </div>
        </div>
        <div className='determine-widget-containers'>
          <div className='header-main border-b-[1px] border-darkcolor-400'>
            <div className='flex flex-row justify-between'>
              <div>
                <h3>DETERMINE sizing summary</h3>
                <h5>
                  The sizing summary provides an overview of the ESP design's
                  key components, along with the downhole and operating
                  conditions.
                </h5>
              </div>
            </div>
          </div>

          <div className='container-body flex flex-col gap-2'>
            <div className='flex flex-col gap-2 pb-4 mb-4'>
              <h5>Project name</h5>
              <h1 className='header-title'>{reportInfo?.ProjectName}</h1>
            </div>
            <div className='flex flex-row gap-4'>
              <div className='flex flex-col gap-4 basis-full lg:basis-1/2'>
                <div className='equipment-section p-2 flex-col gap-2'>
                  <span className="text-[16px] font-bold">Casing/Tubing</span>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Casing</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>{reportInfo?.Casing}</span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Tubing</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>{reportInfo?.Tubing}</span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Casing pressure</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>
                      {reportInfo?.CasingPressure}{" "}
                      {reportInfo?.CasingPressure_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Tubing pressure</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>
                      {reportInfo?.TubingPressure}{" "}
                      {reportInfo?.TubingPressure_Unit}
                    </span>
                  </div>
                </div>

                <div className='equipment-section p-2 flex-col gap-2'>
                  <span className="text-[16px] font-bold">
                    Surface electrical @ {reportInfo?.OperatingFrequency} Hz
                  </span>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Surface voltage</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>
                      {reportInfo?.Surface_SurfaceVoltage}{" "}
                      {reportInfo?.Surface_SurfaceVoltage_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row  py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Drive amperage</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>
                      {reportInfo?.Surface_DriveAmps}{" "}
                      {reportInfo?.Surface_DriveAmps_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Surface power</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>
                      {reportInfo?.Surface_KVARequired}{" "}
                      {reportInfo?.Surface_KVARequired_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Supply voltage</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>
                      {reportInfo?.Surface_SupplyVoltage}{" "}
                      {reportInfo?.Surface_SupplyVoltage_Unit}
                    </span>
                  </div>
                </div>

                <div className='equipment-section p-2 flex-col gap-2'>
                  <span className="text-[16px] font-bold">Equipment</span>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Main pump</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>
                      {reportInfo?.Pump
                        ? `${reportInfo.Pump}, ${reportInfo.PumpNoOfStages} Stages`
                        : ""}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Charge pump</span>
                    <div className='flex flex-col w-3/5 text-neutral-25 text-[14px] font-normal'>
                      <span>
                        {reportInfo?.Gas_ChargePump1
                          ? `${reportInfo.Gas_ChargePump1}, ${reportInfo.Gas_ChargePump1_Stages} stages`
                          : ""}
                      </span>
                      <span>
                        {reportInfo?.Gas_ChargePump2 ? (
                          <>
                            {reportInfo.Gas_ChargePump2},{" "}
                            {reportInfo.Gas_ChargePump2_Stages} stages
                          </>
                        ) : (
                          ""
                        )}
                      </span>
                      <span>
                        {reportInfo?.Gas_ChargePump3 ? (
                          <>
                            {reportInfo.Gas_ChargePump3},{" "}
                            {reportInfo.Gas_ChargePump3_Stages} stages
                          </>
                        ) : (
                          ""
                        )}
                      </span>
                    </div>
                  </div>

                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Gas handler</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>{reportInfo?.GKX}</span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Gas separator</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>
                    {reportInfo?.Gas_GasSeparator} ( {reportInfo?.GasSep_NoOfSeparator} )
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Upper protector</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>
                      {" "}
                      {reportInfo?.UpperProtector}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Lower protector</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>
                      {reportInfo?.LowerProtector}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Motor</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>
                      {reportInfo?.Motor}, {reportInfo?.MotorPower}
                      {reportInfo?.MotorPowerUnit}, {reportInfo?.MotorVoltage}
                      {reportInfo?.MotorVoltageUnit}, {reportInfo?.MotorCurrent}
                      {reportInfo?.MotorCurrentUnit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Sensor</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>{reportInfo?.Motor_Sensor}</span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Controller</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>{reportInfo?.Controller}</span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Harmonic filter</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>
                      {reportInfo?.HarmonicFilterSelection} {`${reportInfo?.SR_HarmonicFilterPN? `(PN: ${reportInfo?.SR_HarmonicFilterPN})` : ""}`}
                    </span>
                  </div>
                  <div className='flex flex-row py-1'>
                    <span className='w-2/5 text-neutral-25 text-[14px] font-normal'>Cable</span>
                    <span className='w-3/5 text-neutral-25 text-[14px] font-normal'>{reportInfo?.Cable}</span>
                  </div>
                </div>
              </div>
              <div className='hidden lg:flex basis-1/2'>
                <img src={pumpIcon} id='pump-icon' alt='pump icon' />
              </div>
            </div>
            <div className='flex flex-col gap-4'>
              <HorizontalTab tabs={tabs} tabChange={setActiveTab} />
              {/* Tab - Well depths */}
              {activeTab === 0 && (
                <div className='determine-widget-containers-2 p-2 flex-col gap-2 mb-5'>
                  <span className="text-neutral-25 text-[14px] font-semibold">Well depths</span>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Pump setting MD</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.PumpSettingMD}{" "}
                      {reportInfo?.PumpSettingMD_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Pump setting VD</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.PumpSettingVD}{" "}
                      {reportInfo?.PumpSettingVD_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">DATUM</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.Datum}{" "}
                      {reportInfo?.Datum_Unit}
                    </span>
                  </div>
                </div>
              )}

              {/* Tab - Well / IPR @Datum */}
              {activeTab === 1 && (
                <div className='determine-widget-containers-2 p-2 flex-col gap-2 mb-5'>
                  <span className="text-neutral-25 text-[14px] font-semibold">Well / IPR @ Datum</span>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">PI</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.ProductivityIndex}{" "}
                      {reportInfo?.PI_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Static BHP</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.StaticBHP}{" "}
                      {reportInfo?.StaticBHP_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Flowing BHP</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.FlowingBHP}{" "}
                      {reportInfo?.FlowingBHP_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Static FL</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.StaticFL}{" "}
                      {reportInfo?.StaticFL_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Producing FL</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.ProductionFL}{" "}
                      {reportInfo?.ProductionFL_Unit}
                    </span>
                  </div>
                </div>
              )}

              {/* Tab - Operatig performance */}
              {activeTab === 2 && (
                <div className='determine-widget-containers-2 p-2 flex-col gap-2 mb-5'>
                  <span className="text-neutral-25 text-[14px] font-semibold">Operating performance</span>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Production rate</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.ProductionRate}{" "}
                      {reportInfo?.ProductionRate_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Barrels of oil per day (BOPD)</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.BOPD}{" "}
                      {reportInfo?.BOPD_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Barrels of water per day (BWPD)</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.BWPD}{" "}
                      {reportInfo?.BWPD_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Gas production</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.GasProduction}{" "}
                      {reportInfo?.GasProduction_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Operating frequency</span>
                    <span className="text-neutral-25 text-[14px] font-normal">{reportInfo?.OperatingFrequency} Hz</span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">FLOP</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.Flop}{" "}
                      {reportInfo?.Flop_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">PIP</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.PIP}{" "}
                      {reportInfo?.Pump_PIP_Unit}
                    </span>
                  </div>
                </div>
              )}

              {/* Tab - Fluid properties */}
              {activeTab === 3 && (
                <div className='determine-widget-containers-2 p-2 flex-col gap-2 mb-5'>
                  <span className="text-neutral-25 text-[14px] font-semibold">Fluid properties</span>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Regional PVT</span>
                    <span className="text-neutral-25 text-[14px] font-normal">{reportInfo?.Region}</span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Oil Api* / SpGrOil</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.OilApi}° / {reportInfo?.OilGravity}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Water gravity</span>
                    <span className="text-neutral-25 text-[14px] font-normal">{reportInfo?.WaterGravity}</span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Water cut</span>
                    <span className="text-neutral-25 text-[14px] font-normal">{reportInfo?.WaterCut} %</span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Bottom hole temperature</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.BottomHoleTemprature}{" "}
                      {reportInfo?.BottomHoleTemprature_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Surface fluid temp</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.SurfaceFluidTemprature}{" "}
                      {reportInfo?.SurfaceFluidTemprature_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Gas gravity</span>
                    <span className="text-neutral-25 text-[14px] font-normal">{reportInfo?.GasGravity}</span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Bubble point</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.BubblePoint}{" "}
                      {reportInfo?.BubblePoint_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">GOR</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.GOR}{" "}
                      {reportInfo?.GOR_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">GLR</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.GLR}{" "}
                      {reportInfo?.GLR_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Fluid composite SG</span>
                    <span className="text-neutral-25 text-[14px] font-normal">{reportInfo?.CompositeSG}</span>
                  </div>
                  <div className='flex flex-row py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Liquid phase SG</span>
                    <span className="text-neutral-25 text-[14px] font-normal">{reportInfo?.LiquidPhaseSG}</span>
                  </div>
                </div>
              )}

              {/* Tab - Downhole operating performance */}
              {activeTab === 4 && (
                <div className='determine-widget-containers-2 p-2 flex-col gap-2 mb-5'>
                  <span className="text-[16px] font-semibold">Downhole operating performance</span>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Total volume at intake </span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.TotalVolumeIntake}{" "}
                      {reportInfo?.GasTFVUnit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Oil rate</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.OilRate}{" "}
                      {reportInfo?.OilRate_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Water rate</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.WaterRate}{" "}
                      {reportInfo?.WaterRate_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Gas rate</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.GasRate}{" "}
                      {reportInfo?.GasRate_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Percent of free gas at intake</span>
                    <span className="text-neutral-25 text-[14px] font-normal">{reportInfo?.FreeGasIntakePercent} %</span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Percent of free gas at main pump</span>
                    <span className="text-neutral-25 text-[14px] font-normal">{reportInfo?.PumpFreeGasPercent} %</span>
                  </div>
                  <div className='flex flex-row row-bottom-line py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Required system TDH</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.RequiredSystemTDH}{" "}
                      {reportInfo?.Pump_RequiredTDH_Unit}
                    </span>
                  </div>
                  <div className='flex flex-row py-1 justify-between'>
                    <span className="text-neutral-25 text-[14px] font-normal">Total system TDH</span>
                    <span className="text-neutral-25 text-[14px] font-normal">
                      {reportInfo?.TotalTDH}{" "}
                      {reportInfo?.Pump_RequiredTDH_Unit}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <SizingSchematic ReportData={reportInfo} />
        <WellInfoPerformanceReport />
        <PerformanceCurve />
        <EquipmentDetails ReportData={reportInfo} />
        <DeviationSurvey ReportData={reportInfo} />
      </>
    );
}


export default SizingSummary;